/**
 * صفحه‌ی یک رخداد یا یک مکان — قرینه‌ی apps/mobile/app/place/[id].tsx.
 *
 * یک فایل برای هر دو، چون تفاوتشان چند فیلد است نه یک صفحه‌ی دیگر (جدولشان
 * هم یکی است — مایگریشن 0042).
 *
 * مهم‌ترین دکمه‌ی صفحه «سیگنال از روی این» است: کلِ کاری که کشف قرار است بکند
 * همین است — از یک چیزِ بیرونی به یک دعوتِ واقعی برسی. عنوان و دسته و مکان
 * پیش‌پر می‌شوند و `eventId` هم با سیگنال ذخیره می‌شود، تا بعداً بشود شمرد
 * چند نفر از این رخداد سیگنال ساخته‌اند (جزئی از امتیازِ محبوبیت).
 */
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAccountGate } from '@/features/signup/SignupGate';
import { useQuery } from '@tanstack/react-query';
import { businessRepo, eventRepo, tagRepo } from '@/core/data/miscRepo';
import { popularityRepo } from '@/core/data/popularityRepo';
import { useSession } from '@/core/session';
import { trackEventOpen } from '@/core/lib/analytics';
import { t, useLocale } from '@/core/lib/i18n';
import { tagLabel } from '@/core/lib/tagLabel';
import { itemFields, type ItemFieldIcon } from '@/core/lib/itemFields';
import { formatDate } from '@/core/lib/dates';
import { SafeImg } from '@/ui/SafeImg';
import {
  EMPTY_STATS, clickRate, openRate, percentLabel, type MyEvent,
} from '@/core/lib/myEvents';
import { directionsWebUrl } from '@/core/lib/directions';
import { withUtm } from '@/core/lib/outboundUrl';
import { descriptionBlocks } from '@/core/lib/richText';
import { Card } from '@/ui/kit';
import { RichText } from '@/ui/RichText';
import { CalendarIcon, PinIcon, PriceIcon } from '@/ui/icons';
import { setCreatePrefill } from '@/features/signals/createPrefill';
import { useNewSignal } from '@/features/signals/NewSignalGuard';
import { trackItemLink } from '@/core/lib/analytics';
import type { ItemLink } from '@/core/lib/itemWatch';
import { OccurrenceList } from './OccurrenceList';
import { useItemDwell } from './useItemDwell';
import { FavButton, useFavorites } from './favorites';
import { IssueButton, useIssues } from './issues';
import { tagIdsOf } from '@/core/domain/types';
import type { Business, EventItem } from '@/core/domain/types';

/** همان رنگ و واژه‌ای که کارتِ پینِ صفحه‌ی کشف به کار می‌برد. */
const MY_TONE: Record<MyEvent['state'], string> = {
  pending: 'count wait', live: 'count ok', editPending: 'count wait', rejected: 'count danger',
};
const MY_LABEL: Record<MyEvent['state'], string> = {
  pending: 'myEventPending', live: 'myEventLive',
  editPending: 'myEventEditPending', rejected: 'myEventRejected',
};

/** اسمِ آیکونی که core می‌دهد → خودِ آیکونِ وب. */
const FIELD_ICON: Record<ItemFieldIcon, typeof PinIcon> = {
  clock: CalendarIcon, pin: PinIcon, tag: PriceIcon,
};

export function ItemPage({ kind }: { kind: 'event' | 'place' }) {
  useLocale();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const gate = useAccountGate();
  const { session, profile } = useSession();
  // «هنوز دوستی نداری» — یه‌بار، قبل از باز شدنِ فرم. نگاه کن به NewSignalGuard.
  const newSignal = useNewSignal();

  // نوعِ صریح لازمه: دو شاخه‌ی queryFn دو نوعِ متفاوت برمی‌گردونن و بدونِ
  // این، TypeScript روی `{}` می‌نشینه و هیچ فیلدی شناخته نمی‌شه.
  const q = useQuery<EventItem | Business | null>({
    queryKey: [kind, id],
    queryFn: () => (kind === 'event' ? eventRepo.byId(id!) : businessRepo.byId(id!)),
    enabled: !!id,
  });

  // بالاتر از هر `return`ِ زودهنگام: هوک‌ها نباید شرطی صدا زده شوند.
  const fav = useFavorites();
  // تگ‌ها برای چیپ‌های زیرِ عنوان — همان کلید و همان فهرستی که بقیه‌ی
  // صفحه‌ها (و صفحه‌ی قرینه‌ی موبایل) استفاده می‌کنند.
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  /** «مشکلِ این رخداد» — فقط صاحبِ پروژه (features/discover/issues). */
  const issues = useIssues();

  /**
   * «این صفحه باز شد» — یکی از اجزای امتیازِ محبوبیت (مایگریشن 0049).
   *
   * تا حالا فقط گوشی می‌شمرد، یعنی محبوبیت فقط رفتارِ کاربرِ موبایل را می‌دید.
   * قرینه‌اش apps/mobile/app/place/[id].tsx است، با همان سه قید:
   *
   *  - یک بار برای هر بار باز شدنِ صفحه، نه هر رندر (همین `counted`): وگرنه
   *    هر بار که نشان‌شده‌ها تازه می‌شد یک بازدیدِ ساختگی ثبت می‌شد.
   *  - منتظرِ خودِ رکورد، نه فقط شناسه‌ی توی آدرس: لینکِ خراب بازدید نیست.
   *  - خطا بلعیده می‌شود: شمردن کارِ ما با داده است و نباید صفحه‌ای که کاربر
   *    باز کرده را خراب کند.
   *
   * و یک قیدِ بیشتر از موبایل: فقط با حساب. این صفحه برای مهمان هم باز است
   * ولی `bump_event_counter` بدونِ `auth.uid()` استثنا می‌دهد — پس بازدیدِ
   * مهمان شمرده نمی‌شود. همان تصمیمی که برای یکتاسازی هم گرفته شده (T16):
   * مهمان کلیدِ یکتای قابلِ اعتمادی ندارد.
   */
  const counted = React.useRef<string | null>(null);
  const item = q.data;

  /**
   * آمارِ رخدادِ خودم. `my_event_stats` خودش فقط به صاحبِ رخداد جواب می‌دهد
   * (مایگریشن 0077)؛ این شرط فقط جلوی درخواستِ بی‌فایده را می‌گیرد.
   */
  const isMine = !!profile && kind === 'event'
    && !!item && 'submittedBy' in item && item.submittedBy === profile.id;
  const myStats = useQuery({
    queryKey: ['myEventStats', item && 'rootId' in item ? item.rootId ?? item.id : null],
    queryFn: () => popularityRepo.myStats([(item as EventItem).rootId ?? item!.id]),
    enabled: isMine,
    staleTime: 60_000,
  });
  /**
   * زمان/مکان‌های اضافه. فهرستِ خالی حالتِ عادی است، نه خطا — رخدادی که یک
   * زمان و یک مکان دارد اصلاً سطری در `event_occurrences` ندارد.
   */
  const occurrences = useQuery({
    queryKey: ['eventOccurrences', id],
    queryFn: () => eventRepo.occurrences(id!),
    enabled: !!id,
  });

  React.useEffect(() => {
    if (!item || !session || counted.current === item.id) return;
    counted.current = item.id;
    popularityRepo.bump(item.id, 'detail_view').catch(() => undefined);
  }, [item, session]);

  /** چند ثانیه این‌جا ماند (WHOSUP-297). */
  useItemDwell(kind, item?.id);

  /**
   * کلیک روی یک لینکِ بیرونی: شمارنده‌ی محبوبیت (فقط با حساب) و رویدادِ
   * `item_link` با این‌که **کدام** لینک بود (WHOSUP-297، مهمان هم).
   */
  const linkClicked = (which: ItemLink) => {
    if (!item) return;
    trackItemLink(kind, item.id, which);
    if (which !== 'directions' && session) {
      popularityRepo.bump(item.id, 'link_click').catch(() => undefined);
    }
  };

  // قیف (WHOSUP-291) — برای مهمان هم، برخلافِ شمارنده‌ی محبوبیتِ بالا.
  const openedId = kind === 'event' ? item?.id : undefined;
  React.useEffect(() => {
    if (openedId) trackEventOpen(String(openedId), 'discover');
  }, [openedId]);

  if (q.isLoading) return <div className="feed"><p className="dim">…</p></div>;
  // `item` بالاتر از افکتِ شمارش تعریف شده؛ این شرط همون‌جا نوعش را هم باریک
  // می‌کند، پس پایین‌تر دیگر `null` نیست.
  if (!item) return <div className="feed"><Card><p className="dim">{t('placeNotFound')}</p></Card></div>;

  const title = 'title' in item ? item.title : item.name;
  /** وضعیتِ همین ردیف، با همان واژگانِ کارتِ پین. */
  const myState: MyEvent['state'] = item.status === 'published' ? 'live'
    : item.status === 'rejected' ? 'rejected' : 'pending';
  const myCounts = myStats.data?.get(
    ('rootId' in item ? item.rootId : undefined) ?? item.id,
  ) ?? EMPTY_STATS;
  // کِی/کجا/چند از core می‌آن، نه از این‌جا: موبایل هم عیناً همین فهرست رو
  // رندر می‌کنه (lib/itemFields.ts).
  const fields = itemFields(item, kind);

  // تگ‌های خودِ آیتم، به همان ترتیبی که tagIdsOf می‌دهد — ژانرهای کرالر هم
  // توی همین فهرست‌اند، فهرستِ تگ حالا یکی است.
  const itemTags = tagIdsOf(item)
    .map((tid) => (tags.data ?? []).find((x) => x.id === tid))
    .filter((x): x is NonNullable<typeof x> => !!x);

  // عیناً همون سه فیلدِ موبایل — نه بیشتر، تا فرمِ دو کلاینت یکسان پر شه.
  const startSignal = () => {
    // این صفحه حالا برای مهمان هم باز می‌شه، ولی ساختِ سیگنال نه. مهمان به
    // ورود می‌ره و بعدش به **همین صفحه** برمی‌گرده، نه یک‌راست به فرم:
    // پیش‌پرکردن یه متغیرِ ماژوله (createPrefill.ts) و ورود با گوگل کلِ صفحه
    // رو دوباره لود می‌کنه، پس تا فرم برسه از بین رفته. یه کلیکِ اضافه،
    // در ازای فرمی که واقعاً پر است.
    if (!session) {
      // برگه‌ی ثبت‌نام با دلیلش (WHOSUP-308) — «عضو شو» به ورود و برگشت به همین‌جا.
      gate('plan', () => undefined);
      return;
    }
    newSignal(() => {
      setCreatePrefill({
        title,
        categoryId: item.categoryId ?? undefined,
        eventId: item.id,
        // «کجا»ی رخداد هم می‌ره تا فرم خالی باز نشه — venue فقط مالِ رخداده،
        // وگرنه همون آدرس. «کِی» رو خودِ فرم با eventId می‌گیره.
        placeName: ('venueName' in item ? item.venueName : undefined)
          ?? item.address ?? undefined,
      });
      nav('/signal/new');
    });
  };

  return (
    <div className="feed">
      <Link to="/discover" className="dim">{t('back')}</Link>

      {item.imageUrl ? (
        <SafeImg className="hero-img" src={item.imageUrl} screen="web.item" />
      ) : null}

      {/* قلب کنارِ عنوان، نه ته صفحه — همان جایی که موبایل گذاشته
          (apps/mobile/app/place/[id].tsx): چیزی که با یک لمس انجام می‌شود
          نباید پشتِ اسکرول باشد. */}
      <div className="item-head">
        <h1 className="feed-title">{title}</h1>
        <FavButton fav={fav} kind={kind === 'event' ? 'event' : 'business'} id={item.id} />
        {/* همان دکمه‌ی کارتِ کشف، این‌بار کنارِ عنوان. فقط روی رخداد: مکان‌ها
            از یک منبعِ دیگر می‌آیند و این فهرست مالِ رخدادهاست. */}
        {kind === 'event' ? <IssueButton issues={issues} eventId={item.id}
          tagIds={item.tagIds} categoryId={item.categoryId} /> : null}
      </div>

      {/* چیپ‌های تگ زیرِ عنوان — همان ردیفی که موبایل دارد
          (apps/mobile/app/place/[id].tsx): «ویژه» اول، بعد تگ‌های خودِ آیتم.
          چیپِ نمایشی است نه دکمه، پس span — همان الگوی FriendsPage. */}
      {('isPromoted' in item && item.isPromoted) || itemTags.length > 0 ? (
        <div className="chiprow chips-wrap">
          {'isPromoted' in item && item.isPromoted
            ? <span className="chip">{t('promoted')}</span> : null}
          {itemTags.map((tag) => (
            <span key={tag.id} className="chip">{tagLabel(tag)}</span>
          ))}
        </div>
      ) : null}

      {/* رخدادِ خودِ کاربر: وضعیت، آمار، و راهِ ویرایش.
          این‌ها قبلاً روی کارتِ پینِ بالای صفحه‌ی کشف بودند و آن فهرست را
          شلوغ می‌کردند (خواسته: آن‌جا فقط عکس و عنوان و وضعیت). جایشان
          این‌جاست — همان‌جا که با یک کلیک از همان کارت می‌رسی. */}
      {isMine ? (
        <Card>
          <span className={MY_TONE[myState]}>{t(MY_LABEL[myState])}</span>
          {myState === 'rejected' && item.reviewNote ? <p>{item.reviewNote}</p> : null}
          {/* دو نسبت، با خودِ عددها کنارشان: درصدِ تنها بدونِ مخرج
              گمراه‌کننده است («۱۰۰٪ بازش کردند» از یک نفر). */}
          <p className="dim">
            {myCounts.impressions === 0 ? t('myEventStatsEmpty') : t('myEventStats', {
              seen: String(myCounts.impressions),
              opened: String(myCounts.detailViews),
              openRate: percentLabel(openRate(myCounts)),
              clicked: String(myCounts.linkClicks),
              clickRate: percentLabel(clickRate(myCounts)),
            })}
          </p>
          {item.endsAt ? (
            <p className="dim">{t('myEventPinNote', { date: formatDate(item.endsAt) })}</p>
          ) : null}
          <Link className="btn" to={`/event/${item.id}/edit`}>{t('myEventEdit')}</Link>
        </Card>
      ) : null}

      {/* سه فیلدِ ساخت‌یافته، بالای توضیحات و در کارتِ خودشون: این‌ها چیزیه که
          خواننده اول می‌خواد، پس نباید لای متن قایم شن. */}
      {fields.length > 0 ? (
        <Card>
          <div className="factlist">
            {fields.map((f) => {
              const Glyph = FIELD_ICON[f.icon];
              return (
                <div className="fact" key={f.name}>
                  <Glyph size={17} />
                  <div className="fact-body">
                    <b className="field-label">{f.label}</b>
                    <span>{f.value}</span>
                    {/* «اینجا اختلاف هست، خودت ببین» — نه هشدار، نه پنهان
                        کردنِ مقدار. نگاه کن به core/lib/fieldAlternatives.ts. */}
                    {f.alternatives.map((a) => (
                      <span className="alt-say" key={`${a.field}-${a.source}-${a.value}`}>
                        {t('altSays', { value: a.value })}
                        {' — '}
                        {a.url
                          ? <a href={withUtm(a.url)} target="_blank" rel="noreferrer"
                              onClick={() => linkClicked('alt_source')}>{a.source} ↗</a>
                          : a.source}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      <Card>
        {/* قالبِ توضیح (WHOSUP-233): نسخه‌ی مارک‌داونِ کرالر اگر بود، وگرنه
            همان ستون با خط‌های تازه‌اش. تا امروز `<p>{description}</p>` بود و
            HTML اینترها را جمع می‌کرد، پس حتی خطِ تازه هم دیده نمی‌شد. */}
        <RichText blocks={descriptionBlocks(item)} />

        {/* چند زمان/مکان — فقط وقتی واقعاً چند تاست (مایگریشن 0054). لینکِ هر
            سطر مالِ خودش است: هر سانس صفحه‌ی فروشِ خودش را دارد. بالای دکمه‌ها
            و پایینِ توضیحات، همان‌جایی که گوشی می‌گذاردش. */}
        {(occurrences.data ?? []).length > 0 ? (
          <>
            <h3>{t('eventOccurrences')}</h3>
            <OccurrenceList rows={occurrences.data ?? []}
              onLinkClick={() => linkClicked('occurrence')} />
          </>
        ) : null}

        <div className="chiprow chips-wrap">
          {/* «Who's in? Send to friends» تا امروز یادداشتِ ته صفحه بود — یعنی
              کسی که تا آخر اسکرول نمی‌کرد هیچ‌وقت نمی‌دیدش، و کسی که می‌دیدش
              دیگه خودِ دکمه جلوی چشمش نبود. حالا همون جمله راهنمای ماوس‌روِ
              خودِ دکمه‌ست، روی هر دکمه‌ی «برنامه‌ی جدید» هرجا که هست: هدر،
              منوی گوشی، کارتِ کشف (features/discover/newPlan.tsx) و این‌جا. */}
          <button type="button" className="btn fill" title={t('whoIsIn')} onClick={startSignal}>
            {t('newSignal')}
          </button>
          {/* بلیط، اگر رخداد جدا داشته باشد (مایگریشن 0077). کلیکش هم مثلِ
              لینکِ معرفی شمرده می‌شود — برای صاحبِ رخداد همان «چند نفر روی
              لینک زدند» است. */}
          {'ticketUrl' in item && item.ticketUrl ? (
            <a className="btn fill" href={withUtm(item.ticketUrl)} target="_blank" rel="noreferrer noopener"
              onClick={() => linkClicked('ticket')}>
              {t('buyTicket')}
            </a>
          ) : null}
          {/* شمردنِ کلیک روی لینکِ منبع — جزءِ پنجمِ امتیازِ محبوبیت.
              همچنان یک `<a>`ِ واقعی می‌ماند و نه یک دکمه: باز کردن در تبِ تازه،
              «کپیِ نشانی» و کلیکِ وسط باید کار کنند. شمارش فقط یک کارِ جانبیِ
              کنارِ همان کلیک است، پس نه `preventDefault` دارد نه انتظارِ جواب.
              مهمان (قبل از ورود) این دکمه را نمی‌بیند (WHOSUP-299). */}
          {item.url && session ? (
            <a className="btn" href={withUtm(item.url)} target="_blank" rel="noreferrer noopener"
              onClick={() => linkClicked('source')}>
              {t('openLink')}
            </a>
          ) : null}
          {/* روی وب همیشه نسخه‌ی مرورگرِ نقشه — طرحِ `geo:`/`maps:` مالِ گوشیه. */}
          {item.lat != null && item.lng != null ? (
            <a className="btn" target="_blank" rel="noreferrer noopener"
              onClick={() => linkClicked('directions')}
              href={directionsWebUrl({ lat: item.lat, lng: item.lng }, null)}>
              {t('directions')}
            </a>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
